import React, { useState } from 'react'
import {
    makeStyles, Box, Button, Card, CardHeader, CardContent, Collapse, Typography, Grid
} from '@material-ui/core'
import ProviderAPI from 'config/api/ProviderAPI'
import { onError } from 'config/lib/errorLib'
import { productCategoryLabels } from 'config/constants/Constants'
import componentStyles from 'assets/theme/views/admin/react-bs-table.js'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { NotificationManager } from 'react-notifications'
import Loader from 'components/general/Loader'
import MissingInfoDialog from './MissingInfoDialog'
import TechnologyCategorySection, { normalizeEntries, cleanTechnology } from 'components/provider/TechnologyCategorySection'

const useStyles = makeStyles(componentStyles)

// Solar technology categories, in display order.
const SOLAR_CATEGORIES = [
    'SolarPanel',
    'SolarBattery',
    'SolarInverter',
    'MonitoringSystem',
    'PowerDiverter',
    'EVCharger',
    'BlackoutProtection',
    'SmartDevice',
    'Optimiser'
]

// Categories that must have at least one entry with a brand selected.
const REQUIRED_CATEGORIES = [
    'SolarPanel',
    'SolarInverter',
    'SolarBattery',
    'MonitoringSystem'
]

function TechnologyCard(props) {
    const classes = { ...useStyles() }
    const [open, setOpen] = useState(true)
    const api = new ProviderAPI()

    const details = props.details || {}
    const brands = props.brands || []
    const profilePanelWarranty = props.companyDetails?.profileDetails?.panelWarranty || ''

    const [loading, setLoading] = useState(false)
    const [showMissingFields, setShowMissingFields] = useState(false)
    const [missingFields, setMissingFields] = useState([])

    function getMissingFields() {
        // Require at least one entry with a brand for each required category.
        return REQUIRED_CATEGORIES
            .filter(cat => !normalizeEntries(details[cat]).some(e => e.brand))
            .map(cat => ({
                key: cat,
                label: `${productCategoryLabels[cat] || cat} Brand`
            }))
    }

    async function saveDetails() {
        if (!props.admin) {
            const missing = getMissingFields()
            if (missing.length > 0) {
                setMissingFields(missing)
                setShowMissingFields(true)
                return
            }
        }

        setLoading(true)
        try {
            const company = await api.updateTechnology(props.companyId, cleanTechnology(details))
            props.updateCompany(company)
            props.updateStage && props.updateStage('projects')
            NotificationManager.success('Technology saved')
        } catch (e) {
            onError(e)
        }
        setLoading(false)
    }

    return (
        <Card>
            <CardHeader
                subheader={
                    <>
                        <Box display='flex' style={{ justifyContent: 'space-between' }}>
                            <Box>
                                <Box component={Typography} variant='h2' marginBottom='0!important'>
                                    <Box component='span'>Company Technology</Box>
                                </Box>
                                <Box
                                    component='p'
                                    fontSize='.875rem'
                                    marginBottom='0'
                                    marginTop='0'
                                    lineHeight='1.7'
                                    fontWeight='300'
                                >
                                    This is the technology your company uses in its installations. Add multiple entries per category if you offer more than one. Leave any sections that are not applicable blank
                                </Box>
                            </Box>
                        </Box>
                    </>
                }
                onClick={() => setOpen(o => !o)}
                style={{ cursor: 'pointer' }}
                action={<ExpandMoreIcon style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', marginTop: 4 }} />}
                classes={{ root: classes.cardHeaderRoot }}
            ></CardHeader>

            <Collapse in={open}>
                <CardContent>
                    <Grid container spacing={3}>
                        {SOLAR_CATEGORIES.map(cat => (
                            <TechnologyCategorySection
                                key={cat}
                                cat={cat}
                                companyId={props.companyId}
                                brands={brands}
                                technology={details}
                                updateField={props.updateField}
                                warrantyDefault={cat === 'SolarPanel' ? profilePanelWarranty : ''}
                            />
                        ))}

                        <Grid item xs={12}>
                            <Box display='flex' justifyContent='flex-end'>
                                <Button
                                    variant='contained'
                                    color='primary'
                                    disabled={loading}
                                    onClick={saveDetails}
                                >
                                    {!loading ? <>{(props.admin || props.profile) ? 'Save' : 'Save & Continue'}</> : <Loader height={12} />}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Collapse>
            <MissingInfoDialog
                open={showMissingFields}
                onClose={() => setShowMissingFields(false)}
                missingFields={missingFields}
            />
        </Card>
    )
}

export default TechnologyCard
